import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API_URL from "../api/baseURL";
import axios from "axios";

export default function NotificationsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'

  useEffect(() => {
    if (!token) return;
    fetchNotifications();
  }, [token]);

  const fetchNotifications = () => {
    fetch(`${API_URL}/notifications`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(setNotifications)
      .finally(() => setLoading(false));
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await axios.patch(
          `${API_URL}/notifications/${notification._id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setNotifications(prev =>
          prev.map(n => (n._id === notification._id ? { ...n, read: true } : n))
        );
      } catch (err) {
        console.error("Failed to mark notification as read:", err);
      }
    }
    // Navigate to linked page
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        notifications.filter(n => !n.read).map(n =>
          axios.patch(
            `${API_URL}/notifications/${n._id}/read`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error("Failed to mark all as read:", err);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'request_accepted': { icon: '✓', bg: 'bg-green-500', color: 'text-green-500' },
      'request_rejected': { icon: '✕', bg: 'bg-red-500', color: 'text-red-500' },
      'request_rescheduled': { icon: '📅', bg: 'bg-blue-500', color: 'text-blue-500' },
      'quotation_received': { icon: '📄', bg: 'bg-purple-500', color: 'text-purple-500' },
      'quotation_decision': { icon: '📄', bg: 'bg-purple-500', color: 'text-purple-500' },
      'quotation_updated': { icon: '📄', bg: 'bg-purple-500', color: 'text-purple-500' },
      'quotation_revised': { icon: '📄', bg: 'bg-orange-500', color: 'text-orange-500' },
      'quotation_action': { icon: '📄', bg: 'bg-purple-500', color: 'text-purple-500' },
      'quotation_accepted': { icon: '✓', bg: 'bg-green-500', color: 'text-green-500' },
      'invoice_created': { icon: '🧾', bg: 'bg-indigo-500', color: 'text-indigo-500' },
      'payment_confirmed': { icon: '💳', bg: 'bg-green-600', color: 'text-green-600' },
      'insurance_expiring': { icon: '🛡️', bg: 'bg-yellow-500', color: 'text-yellow-500' },
      'technical_expiring': { icon: '🔧', bg: 'bg-yellow-500', color: 'text-yellow-500' },
    };
    return icons[type] || { icon: '🔔', bg: 'bg-gray-500', color: 'text-gray-500' };
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (days < 30) return `${Math.floor(days / 7)} week${Math.floor(days / 7) > 1 ? 's' : ''} ago`;
    return new Date(date).toLocaleDateString();
  };

  const filteredNotifications = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-2xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm mb-4 p-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'all'
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === 'unread'
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              Unread {unreadCount > 0 && `(${unreadCount})`}
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {filteredNotifications.length === 0 ? (
            <div className="py-16 text-center">
              <div className="text-6xl mb-4">🔔</div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </h3>
              <p className="text-gray-500">
                {filter === 'unread'
                  ? 'You\'re all caught up!'
                  : 'When you receive notifications, they\'ll appear here.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((n) => {
                const { icon, bg } = getNotificationIcon(n.type);
                return (
                  <div
                    key={n._id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-gray-50 ${!n.read ? 'bg-blue-50/50' : ''
                      }`}
                  >
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full ${bg} flex items-center justify-center text-white text-xl shadow-sm`}>
                      {icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-relaxed ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {n.message}
                      </p>
                      <p className={`text-sm mt-1 ${!n.read ? 'text-blue-500 font-medium' : 'text-gray-400'}`}>
                        {getTimeAgo(n.createdAt)}
                      </p>
                    </div>

                    {/* Unread Indicator & Arrow */}
                    <div className="flex items-center gap-2">
                      {!n.read && (
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      )}
                      {n.link && (
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
